<template lang="pug">
ValidationObserver.time-entries-timeline(
  ref="timelineObserver"
  v-slot="{ errors }"
  tag="div"
)
  .popover.vue-popover.duration-popover(
    v-if="newTimeEntry"
    ref="popoverEl"
  )
    .popover-content
      AppDurationInput(
        v-model="newTimeEntryDuration"
        required
        validate-day
        :label="null"
        :show-errors="false"
      )
    .popper__arrow(x-arrow)
  svg.time-entries-timeline--chart(
    focusable="false"
    tabindex="-1"
  )
  .row.mt-40(v-if="newTimeEntry")
    .col-xs-12
      .form-group.has-error(v-if="errors.appDurationInput && errors.appDurationInput.length > 0")
        span.help-block {{ errors.appDurationInput[0] }}
</template>

<script>
import * as d3Selection from 'd3-selection';
import { mapGetters } from 'vuex';
import { sortBy } from 'lodash-es';
import ColorUtils from '../../mixins/color_utils';
import D3Tooltip from '../../utils/d3_tooltip';
import D3Popover from '../../utils/d3_popover';
import { formatDatetimeRange, formatTime, getDuration, stripZone } from '../../utils/time_utils';
import {
  AXIS_COLOR,
  CHART_HEIGHT,
  CHART_PADDING_TOP,
  CHART_BG_COLOR,
  RECT_RADIUS_X,
  RECT_RADIUS_Y,
  NEW_STROKE_WIDTH,
  NEW_FILL_COLOR,
  NEW_STROKE_COLOR,
  NEW_DEFAULT_MINUTES,
  NEW_LINE_LENGTH,
  NEW_TIME_ENTRY_RECT_CLASS,
  NEW_MINIMUM_ENTRY_MINUTES,
  drawTimeline,
} from '~/utils/timeline_graph_utils';
import { MANUAL_TIME_REQUEST_TIME_ENTRY_COLOR } from '~/app_constants';

// Polyfill
const ResizeObserver = window.ResizeObserver;
const STANDARD_TIMEZONE = 'UTC';
const DELETE_TIME_COLOR = '#ff4949';

export default {
  name: 'TimeEntriesTimeline',
  props: {
    timeEntries: {
      type: Array,
      required: true,
    },
    date: {
      required: true,
    },
    clickToAdd: {
      type: Boolean,
      required: false,
      default: false,
    },
    newTimeEntry: {},
    mode: {
      type: String,
      default: 'edit',
    },
    originalTimeEntry: {},

    // selectedTz, ManualTimeRequestProjectName, ManualTimeRequestTaskName props were created
    // to avoid loading the initial state from the server and make one more request
    // This is used in the View Manual Time Request dialog
    selectedTz: {
      type: String,
      required: false,
    },
    tooltipClass: {
      type: String,
      required: false,
      default: '',
    },
    manualTimeRequestProjectName: {
      type: String,
      required: false,
    },
    manualTimeRequestTaskName: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      newTime: null,
      svg: null,
      xScale: null,
      newRect: null,
      durationPopup: null,
      tooltips: [],
      resizeObserver: null,
    };
  },
  computed: {
    ...mapGetters(['getOrganization', 'getProject', 'getTask', 'organizations', 'tasks', 'currentZone']),
    ...mapGetters({
      newTimeEntryTemplate: 'newTimeEntry',
    }),
    currentZoneName() {
      if (this.selectedTz) {
        return this.selectedTz;
      }

      return this.currentZone?.name || 'Etc/UTC';
    },
    dateWithoutZone() {
      if (this.date) {
        return stripZone(this.date);
      }

      return null;
    },
    timelineStart() {
      return moment(this.dateWithoutZone).startOf('day');
    },
    timelineEnd() {
      return moment(this.dateWithoutZone).endOf('day');
    },
    tickValues() {
      return [
        moment(this.dateWithoutZone).set({ hour: 6, minute: 0 }),
        moment(this.dateWithoutZone).set({ hour: 12, minute: 0 }),
        moment(this.dateWithoutZone).set({ hour: 18, minute: 0 }),
      ];
    },
    augmentedTimeEntries() {
      let timeEntries = this.timeEntries.map((timeEntry) => this.augmentTimeEntry(timeEntry));

      if (this.newTimeEntry && this.newTimeEntry.id) {
        timeEntries = timeEntries.filter((timeEntry) => timeEntry.id !== this.newTimeEntry.id);
      }

      if (this.splitMode || this.deleteMode) {
        if (this.newTimeEntry.start_time.isAfter(this.originalTimeEntry.start_time)) {
          const timeEntry = Object.assign({}, this.originalTimeEntry, {
            start_time: this.originalTimeEntry.start_time,
            stop_time: this.newTimeEntry.start_time,
          });

          timeEntries.push(this.augmentTimeEntry(timeEntry));
        }

        if (this.newTimeEntry.stop_time.isBefore(this.originalTimeEntry.stop_time)) {
          const timeEntry = Object.assign({}, this.originalTimeEntry, {
            start_time: this.newTimeEntry.stop_time,
            stop_time: this.originalTimeEntry.stop_time,
          });

          timeEntries.push(this.augmentTimeEntry(timeEntry));
        }
      }

      return timeEntries;
    },
    augmentedNewtimeEntry() {
      if (this.newTimeEntry) {
        return this.augmentTimeEntry(this.newTimeEntry);
      }

      return null;
    },
    newTimeEntryDuration: {
      get() {
        return getDuration(this.newTimeEntry.start_time, this.newTimeEntry.stop_time);
      },
      set(newDuration) {
        this.$emit('input', newDuration);
      },
    },
    editMode() {
      return this.mode === 'edit';
    },
    splitMode() {
      return this.mode === 'split';
    },
    deleteMode() {
      return this.mode === 'delete';
    },
    tooltipOptions() {
      return {
        className: this.tooltipClass,
      };
    },
  },
  watch: {
    newTimeEntry() {
      if (this.splitMode || this.deleteMode) {
        this.drawChart();
      } else {
        this.updateNewTimeEntryPill();
      }
    },
    date() {
      this.drawChart();
    },
    timeEntries() {
      this.drawChart();
    },
    mode() {
      this.drawChart();
    },
  },
  mounted() {
    this.setupResizeObserver();
  },
  beforeDestroy() {
    if (this.durationPopup) {
      this.durationPopup.destroy();
    }
    this.stopResizeObserver();
    this.cleanupTooltips();
  },
  methods: {
    setupResizeObserver() {
      // This ResizeObserver serves two purposes:
      // 1. To re-render the chart when the element's dimensions change
      // 2. Detect when we've come out of a display: none situation and need to re-render (e.g., if the timeline is in an initially-hidden dialog)
      //
      // NOTE: ResizeObserver will make an initial callback call when initialized.
      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(this.onResize);
      });
      this.resizeObserver.observe(this.$el);
    },
    stopResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },
    onResize() {
      this.drawChart();
    },
    augmentTimeEntry(timeEntry) {
      const organization = this.getOrganization(timeEntry.organization_id);
      let project;

      if (timeEntry.project_id) {
        project = this.getProject(Number(timeEntry.project_id));
      }

      if (timeEntry.manual_time_request && this.manualTimeRequestProjectName) {
        project = { text: this.manualTimeRequestProjectName };
      }

      if (project) {
        let projectColor;

        if (timeEntry.manual_time_request) {
          projectColor = timeEntry.project_name
            ? ColorUtils.methods.stringToColor(timeEntry.project_name)
            : MANUAL_TIME_REQUEST_TIME_ENTRY_COLOR;
        } else {
          projectColor = ColorUtils.methods.stringToColor(project.text || '');
        }

        project = Object.assign({}, project, { color: projectColor });
      }

      let task = this.getTask(timeEntry.task_id);

      if (timeEntry.manual_time_request && this.manualTimeRequestTaskName) {
        task = { text: this.manualTimeRequestTaskName };
      }

      return Object.assign({}, timeEntry, {
        organization,
        project,
        task,
      });
    },
    pinTime(time, minuteInterval = 15) {
      time = moment(time);
      const minute = time.minute() - (time.minute() % minuteInterval);
      const pinnedTime = moment(time).set({ minute, second: 0, millisecond: 0 });

      return pinnedTime;
    },
    drawChart() {
      const width = this.$el.getBoundingClientRect().width;

      if (!this.date || width === 0) {
        return;
      }

      const that = this;
      const now = stripZone(moment.tz(this.currentZoneName));
      let pill;
      const pillNewStartTime = d3Selection.local();
      // let elTooltip = d3Selection.local();
      const svg = d3Selection.select(this.$el).select('svg');

      this.svg = svg;

      this.cleanupTooltips();
      // svg.selectAll('*').remove();

      const { bg, chart, nowTooltip, elTooltip, xAxis, xScale, nowGroup } = drawTimeline(svg, {
        date: this.timelineStart.toDate(),
        width,
        now,
        start: this.timelineStart,
        stop: this.timelineEnd,
        tickFormat: TimeUtils.UserTimeFormats.time,
      });

      this.xScale = xScale;

      if (nowTooltip) {
        this.tooltips.push(nowTooltip);
      }

      const rect = chart
        .insert('g')
        .attr('class', 'time-entries')
        .selectAll('rect')
        .data(this.augmentedTimeEntries)
        .enter()
        .insert('rect')
        .call(this.styleTimeEntryRect)
        .each(function (d, _i, _nodes) {
          const tooltipContentElements = [];

          if (d.project) {
            tooltipContentElements.push(d.project.text);
          }

          if (d.task && d.task.text) {
            tooltipContentElements.push(d.task.text);
          }

          let lastLine = formatDatetimeRange(d.start_time, d.stop_time, {
            style: 'time',
            showSeconds: 'auto',
            timezone: that.currentZoneName,
          });

          if (d.manual_tracked && d.manual_tracked === d.overall_tracked) {
            lastLine += ' (manual)';
          }
          tooltipContentElements.push(lastLine);

          const tooltip = new D3Tooltip(this, tooltipContentElements.join('\n'), that.tooltipOptions);

          elTooltip.set(this, tooltip);
          that.tooltips.push(tooltip);
        });

      if (this.clickToAdd) {
        const height = CHART_HEIGHT - NEW_STROKE_WIDTH;
        const width = height;
        const pillY = NEW_STROKE_WIDTH / 2;

        pill = chart
          .append('g')
          .attr('class', 'new-pill')
          .attr('display', 'none')
          .on('click', function () {
            const newStartTime = pillNewStartTime.get(this);
            const now = moment();

            if (newStartTime) {
              let newStopTime = moment(newStartTime).add(NEW_DEFAULT_MINUTES, 'minutes');
              const overlappingTimeEntries = that.findOverlappingTimeEntries(newStartTime, newStopTime);

              if (overlappingTimeEntries.length > 0) {
                const overlappingTimeEntry = overlappingTimeEntries[0];

                newStopTime = moment(overlappingTimeEntry.startTime);
              }

              newStartTime.tz(that.currentZoneName, true);
              newStopTime.tz(that.currentZoneName, true);

              if (newStopTime.isAfter(now)) {
                newStopTime = now.set({ second: 0, millisecond: 0 });
                now.tz(that.currentZoneName);
              }

              that.addTime(newStartTime, newStopTime);
            }
          });

        // eslint-disable-next-line no-unused-vars
        const pillBg = pill
          .append('rect')
          .attr('class', 'new-pill-bg')
          .attr('y', CHART_PADDING_TOP)
          .attr('x', 0)
          .attr('height', height)
          .attr('width', width)
          .attr('rx', RECT_RADIUS_X)
          .attr('ry', RECT_RADIUS_Y)
          .attr('fill', NEW_FILL_COLOR)
          .attr('stroke', NEW_STROKE_COLOR)
          .attr('paint-order', 'stroke')
          .attr('stroke-width', 1);
        const lineX = width / 2;
        const lineY = (CHART_HEIGHT + CHART_PADDING_TOP - NEW_LINE_LENGTH / 2) / 2;
        const lineY2 = lineY + NEW_LINE_LENGTH + NEW_STROKE_WIDTH;
        // eslint-disable-next-line no-unused-vars
        const pillCross = pill
          .append('line')
          .attr('x1', lineX)
          .attr('x2', lineX)
          .attr('y1', lineY)
          .attr('y2', lineY2)
          .attr('stroke', NEW_STROKE_COLOR)
          .attr('stroke-width', NEW_STROKE_WIDTH)
          .clone()
          .attr('transform', `rotate(90, ${lineX}, ${(lineY + lineY2) / 2})`);

        bg.on('mousemove', function (event) {
          const { offsetX: xPoint } = event;

          pill.each(function () {
            const thisSelection = d3Selection.select(this);
            const pointTime = that.xScale.invert(xPoint);
            const pinnedTime = that.pinTime(pointTime);
            let newStartTime = moment.max(pinnedTime, that.timelineStart);
            let overlappingTimeEntries = that.findOverlappingTimeEntries(newStartTime, newStartTime);

            if (overlappingTimeEntries.length > 0) {
              // Try to fit it
              newStartTime = moment(overlappingTimeEntries[0].stopTime).add(1, 'minute');
              overlappingTimeEntries = that.findOverlappingTimeEntries(
                newStartTime,
                moment(newStartTime).add(NEW_MINIMUM_ENTRY_MINUTES, 'minutes'),
              ); // ¯\_(ツ)_/¯

              // Can't fit -- abort
              if (overlappingTimeEntries.length > 0) {
                thisSelection.attr('display', 'none');

                return;
              }
            }

            const newX = that.xScale(newStartTime) + NEW_STROKE_WIDTH / 2 - width / 2;

            pillNewStartTime.set(this, newStartTime);
            thisSelection.attr('display', null);
            thisSelection.attr('transform', `translate(${newX}, ${pillY})`);

            let tooltip = elTooltip.get(this);
            const newTooltipContent = `${formatTime(newStartTime.tz(STANDARD_TIMEZONE), {
              showSeconds: false,
            })} - Add time`;

            if (tooltip) {
              tooltip.updateTitleContent(newTooltipContent);
            } else {
              tooltip = new D3Tooltip(this, newTooltipContent, that.tooltipOptions);
              elTooltip.set(this, tooltip);
              that.tooltips.push(tooltip);
            }
          });
        });

        const hidePill = function () {
          pill.each(function () {
            const thisSelection = d3Selection.select(this);

            thisSelection.attr('display', 'none');
            // Ensure we hide the tooltip even if hovering over it
            const tooltip = elTooltip.get(this);

            if (tooltip) {
              tooltip.hide();
            }
          });
        };

        chart.on('mouseleave', hidePill);
        pill.on('mouseleave', hidePill);
        rect.on('mouseenter', hidePill);

        if (nowGroup) {
          nowGroup.on('mouseenter', hidePill);
        }
      }

      if (this.newTimeEntry) {
        this.newRect = chart
          .insert('g')
          .append('rect')
          .datum(this.augmentedNewtimeEntry)
          .attr('class', NEW_TIME_ENTRY_RECT_CLASS)
          .call(this.styleTimeEntryRect, true)
          .each(function (_d, _i, _nodes) {
            if (that.durationPopup) {
              that.durationPopup.reference = this;
              that.durationPopup.scheduleUpdate();
            } else {
              const popoverEl = that.$refs.popoverEl;

              that.durationPopup = new D3Popover(this, popoverEl, {
                placement: 'bottom',
              });
            }
          });
      }

      // Ensure we disable tab-to-focus on rect elements
      svg.selectAll('rect').attr('tabindex', '-1');

      rect
        .exit()
        .each(function () {
          const thisTooltip = elTooltip.get(this);

          if (thisTooltip) {
            thisTooltip.dispose();
          }
        })
        .remove();

      svg
        .append('g')
        .attr('class', 'x axis')
        .attr('transform', `translate(0, ${CHART_HEIGHT + CHART_PADDING_TOP})`)
        .call(xAxis)
        .call((graph) => {
          graph.select('.domain').remove();
          graph.selectAll('.tick line').attr('stroke', CHART_BG_COLOR);
          graph.selectAll('.tick text').attr('fill', AXIS_COLOR);
        });
    },
    updateNewTimeEntryPill() {
      if (this.newRect) {
        this.newRect.datum(this.augmentedNewtimeEntry).call(this.styleTimeEntryRect, true);
        this.durationPopup.scheduleUpdate();
      }
    },
    styleTimeEntryRect(rect, isNewTimeEntry = false) {
      const pillColor = (timeEntry, fallbackColor) => {
        if (isNewTimeEntry && this.deleteMode) {
          return DELETE_TIME_COLOR;
        }
        if (timeEntry.project) {
          return timeEntry.project.color;
        }

        return fallbackColor;
      };

      rect
        .attr('y', NEW_STROKE_WIDTH / 2 + CHART_PADDING_TOP)
        .attr('height', CHART_HEIGHT - NEW_STROKE_WIDTH)
        .attr('rx', RECT_RADIUS_X)
        .attr('ry', RECT_RADIUS_Y)
        .attr('width', (timeEntry) => {
          if (!timeEntry.start_time || !timeEntry.stop_time) {
            return 1;
          }

          let startTime = stripZone(timeEntry.start_time);
          let stopTime = stripZone(timeEntry.stop_time);

          // Clamp
          if (startTime.isBefore(this.timelineStart)) {
            startTime = this.timelineStart;
          }
          if (stopTime.isAfter(this.timelineEnd)) {
            stopTime = this.timelineEnd;
          }

          const width = this.xScale(stopTime) - this.xScale(startTime) - NEW_STROKE_WIDTH;

          return Math.max(width, 1);
        })
        .attr('x', (timeEntry) => {
          if (!timeEntry.start_time) {
            return 0;
          }

          const startTime = stripZone(timeEntry.start_time);

          return this.xScale(startTime.toDate());
        })
        .attr('fill', (timeEntry) => pillColor(timeEntry, NEW_FILL_COLOR))
        .attr('stroke', (timeEntry) => pillColor(timeEntry, NEW_STROKE_COLOR))
        .attr('stroke-width', NEW_STROKE_WIDTH);
    },
    findOverlappingTimeEntries(startTime, stopTime) {
      startTime = moment(startTime).tz(STANDARD_TIMEZONE);
      stopTime = moment(stopTime).tz(STANDARD_TIMEZONE);

      const result = this.augmentedTimeEntries.reduce((accumulator, timeEntry) => {
        const timeEntryStartTime = stripZone(timeEntry.start_time);
        const timeEntryStopTime = stripZone(timeEntry.stop_time);
        // ----[   ]-----( [  )   ]---(  [ ]  )----[  ( ]  )
        const match =
          timeEntryStartTime.isBetween(startTime, stopTime) ||
          timeEntryStopTime.isBetween(startTime, stopTime) ||
          (startTime.isBetween(timeEntryStartTime, timeEntryStopTime) &&
            stopTime.isBetween(timeEntryStartTime, timeEntryStopTime));

        if (match) {
          accumulator.push({ startTime: timeEntryStartTime, stopTime: timeEntryStopTime });
        }

        return accumulator;
      }, []);

      return sortBy(result, (timeEntry) => timeEntry.start_time);
    },
    addTime(startTime, stopTime) {
      const timeEntry = Object.assign({}, this.newTimeEntryTemplate, {
        start_time: startTime,
        stop_time: stopTime,
      });

      this.$store.dispatch('openDialog', {
        dialog: 'timeEntriesFormDialog',
        type: 'provided',
        timeEntry,
      });
    },
    cleanupTooltips() {
      this.tooltips.forEach((tooltip) => tooltip.dispose());
      this.tooltips.splice(0);
    },
  },
};
</script>
